// REACH-S1 (docs/design/2026-10-28-agent-reach.md §2; founder 2026-10-29): Setup › Reach — the
// agent's Reach in force, so admins can see it and tighten it. Every agent that existed before
// Reach shipped was backfilled with what it allowed then (everyone could ask a DE), so this is
// where it gets narrowed. One line per field: Serves; Hears from; Talks to; Who sees. Each
// audience is chips (Everyone, people, seats, teams) with a hover × to take one off for those who
// may edit; "Everyone" off falls back to the people it serves. The people above the agent always
// reach it and see its work (the (i) says so). Adding named people is R2's picker.
// Saves under `If-Match`; a stale version reloads the current reach.
import type { OperationOutput } from "@companyos/sdk";
import { ApiError } from "@companyos/sdk";
import { Button, InfoTip, TagChip } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";

type View = OperationOutput<"agentReach.get">;
type Audience = View["hears_from"];
type Field = "hears_from" | "talks_to" | "visible_to";

const reachKey = (co: string, id: string) => ["agent-reach", co, id] as const;

const FIELDS: { key: Field; label: string; info: string }[] = [
  {
    key: "hears_from",
    label: "Hears from",
    info: "Who can mention or message it. Anyone else is told to ask for access. The people above it always can.",
  },
  {
    key: "talks_to",
    label: "Talks to",
    info: "Whom it may mention or message. Asking upward is always allowed.",
  },
  {
    key: "visible_to",
    label: "Who sees",
    info: "Who sees its runs besides the people above it and the people it serves.",
  },
];

/** The serving scope as an audience ("Everyone" off narrows to it). */
function servingAudience(v: View): Audience {
  const none: Audience = { company: false, people: [], seats: [], teams: [] };
  const id = v.serves.id;
  if (!id) return none;
  if (v.serves.kind === "person") return { ...none, people: [id] };
  if (v.serves.kind === "seat") return { ...none, seats: [id] };
  return { ...none, teams: [id] };
}

export function ReachSection({
  api,
  company,
  membershipId,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  membershipId: string;
  name: string;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const reach = useQuery({
    queryKey: reachKey(co, membershipId),
    queryFn: () => api.client.agentReach.get({ params: { co, id: membershipId } }),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (body: Partial<Record<Field, Audience>>) =>
      api.client.agentReach.update({
        params: { co, id: membershipId },
        ifMatch: reach.data?.version ?? 0,
        body,
      }),
    onSuccess: (v) => qc.setQueryData(reachKey(co, membershipId), v),
    onError: (e) => {
      if (e instanceof ApiError && e.code === "conflict")
        void qc.invalidateQueries({ queryKey: reachKey(co, membershipId) });
    },
  });

  if (reach.isPending) return <Loading label="Loading reach" />;
  if (reach.error) {
    if (reach.error instanceof ApiError && reach.error.status === 404)
      return (
        <p className="text-sm text-muted-foreground">
          Only the people above {name}, those it serves and admins see its reach.
        </p>
      );
    return <LoadError what="the reach" error={reach.error} />;
  }
  const v = reach.data;
  const named = (id: string) => v.names[id] ?? "Unknown";
  const set = (field: Field, next: Audience) => save.mutate({ [field]: next });

  return (
    <div data-slot="reach-section" className="flex flex-col gap-3">
      <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-3 text-sm">
        <dt className="flex items-center gap-1 text-muted-foreground">
          Serves
          <InfoTip label="About Serves">
            Who gets its notifications and decides access requests. The people above it can always
            look in and step in.
          </InfoTip>
        </dt>
        <dd className="text-foreground">
          {v.serves_name ?? "The company"}
          {v.stored ? null : <span className="ml-2 text-xs text-muted-foreground">Default</span>}
        </dd>
        {FIELDS.map((f) => {
          const aud = v[f.key];
          const editable = v.editable.includes(f.key);
          const without = (list: "people" | "seats" | "teams", id: string) => ({
            ...aud,
            [list]: aud[list].filter((x) => x !== id),
          });
          const chips = (["people", "seats", "teams"] as const).flatMap((list) =>
            aud[list].map((id) => (
              <TagChip
                key={`${list}:${id}`}
                name={named(id)}
                color="zinc"
                {...(editable && !save.isPending
                  ? {
                      onRemove: () => set(f.key, without(list, id)),
                      removeLabel: `Remove ${named(id)} from ${f.label}`,
                    }
                  : {})}
              />
            )),
          );
          const empty = !aud.company && chips.length === 0;
          return [
            <dt key={`${f.key}-t`} className="flex items-center gap-1 text-muted-foreground">
              {f.label}
              <InfoTip label={`About ${f.label}`}>{f.info}</InfoTip>
            </dt>,
            <dd key={`${f.key}-d`} className="flex min-w-0 flex-wrap items-center gap-1">
              {aud.company ? (
                <TagChip
                  name="Everyone"
                  color="sky"
                  {...(editable && !save.isPending
                    ? {
                        onRemove: () => set(f.key, { ...servingAudience(v), company: false }),
                        removeLabel: `Narrow ${f.label} to the people it serves`,
                      }
                    : {})}
                />
              ) : null}
              {chips}
              {empty ? (
                <span className="text-xs text-muted-foreground">Only the people above it</span>
              ) : null}
              {editable && !aud.company ? (
                <Button
                  size="xs"
                  variant="ghost"
                  disabled={save.isPending}
                  aria-label={`Open ${f.label} to everyone`}
                  onClick={() => set(f.key, { ...aud, company: true })}
                >
                  <PlusIcon data-icon="inline-start" aria-hidden="true" />
                  Everyone
                </Button>
              ) : null}
            </dd>,
          ];
        })}
      </dl>
      {v.chain.length ? (
        <p className="text-xs text-muted-foreground">
          Always: {v.chain.map((c) => c.name).join(", ")}
        </p>
      ) : null}
      {save.error ? (
        <p role="alert" className="text-xs text-danger-text">
          {save.error instanceof ApiError && save.error.code === "conflict"
            ? "It changed meanwhile: reloaded, try again."
            : "Could not save."}
        </p>
      ) : null}
    </div>
  );
}
